/**
 * <vehicle-year-picker> — Lightweight blog-embedded vehicle selector.
 * Completely independent from ai-intake-bar (homepage wizard).
 *
 * Usage in blog HTML:
 *   <vehicle-year-picker data-model="Model Y"></vehicle-year-picker>
 *
 * Attributes:
 *   data-model       — Pre-selected model (e.g. "Model Y", "Model 3")
 *   data-heading      — Custom heading text (default: "Find your exact fit")
 *   data-storefront-url   — Shopify Storefront API URL
 *   data-storefront-token — Storefront access token
 */
class VehicleYearPicker extends HTMLElement {
  connectedCallback() {
    this._model = this.dataset.model || 'Model Y';
    this._storefrontUrl = this.dataset.storefrontUrl || 'https://020a3a.myshopify.com/api/2025-10/graphql.json';
    this._storefrontToken = this.dataset.storefrontToken || '206ce4bb92c2c7f83cf48f14ff45b1f9';
    this._heading = this.dataset.heading || 'Find your exact fit';
    this._vehicles = [];
    this._selectedYear = null;

    this.innerHTML = `<div class="vyp__wrap"><div class="vyp__loading">Loading...</div></div>`;
    this._fetchAndRender();
  }

  async _fetchAndRender() {
    try {
      const res = await fetch(this._storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this._storefrontToken,
        },
        body: JSON.stringify({
          query: `{ metaobjects(type: "vehicle", first: 250) { nodes { handle fields { key value } } } }`,
        }),
      });
      const data = await res.json();
      const nodes = data?.data?.metaobjects?.nodes || [];

      this._vehicles = nodes.map((node) => {
        const fields = {};
        (node.fields || []).forEach((f) => { fields[f.key] = f.value; });
        return {
          handle: node.handle,
          year: parseInt(fields.year, 10),
          make: fields.make || '',
          model: fields.model || '',
          fitment: fields.fitment || '',
        };
      }).filter((v) => v.year && v.model && v.fitment);

      this._renderYears();
    } catch (e) {
      this.innerHTML = '';
    }
  }

  _getBaseModel(model) {
    return model.replace(/\s*\d+-seat(er)?/i, '').replace(/\s+/g, ' ').trim();
  }

  _normalize(s) {
    return s.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  _renderYears() {
    const matching = this._vehicles.filter(
      (v) => this._normalize(this._getBaseModel(v.model)) === this._normalize(this._model)
    );
    const years = [...new Set(matching.map((v) => v.year))].sort((a, b) => b - a);

    if (!years.length) { this.innerHTML = ''; return; }

    const wrap = this.querySelector('.vyp__wrap');
    wrap.innerHTML = `
      <div class="vyp__heading">${this._esc(this._heading)}</div>
      <div class="vyp__pills" data-step="year">
        ${years.map((y) => `<button class="vyp__pill" type="button" data-value="${y}">${y}</button>`).join('')}
      </div>
    `;

    this._revealPills(wrap.querySelector('[data-step="year"]'));
    wrap.querySelectorAll('[data-step="year"] .vyp__pill').forEach((btn) => {
      btn.addEventListener('click', () => this._onYearSelect(parseInt(btn.dataset.value, 10)));
    });
  }

  _onYearSelect(year) {
    this._selectedYear = year;

    const matching = this._vehicles.filter(
      (v) => this._normalize(this._getBaseModel(v.model)) === this._normalize(this._model) && v.year === year
    );
    const fitments = [...new Set(matching.map((v) => v.fitment))];

    if (fitments.length === 1) {
      this._navigate(matching[0].handle);
      return;
    }

    const LABELS = {
      '5-seater': '5-Seater', '7-seater': '7-Seater',
      'highland': 'Highland', '6-seat': '6-Seat',
    };

    const wrap = this.querySelector('.vyp__wrap');
    wrap.innerHTML = `
      <div class="vyp__heading">${this._esc(this._heading)}</div>
      <div class="vyp__context">${year} Tesla ${this._esc(this._model)}</div>
      <div class="vyp__label">Which configuration?</div>
      <div class="vyp__pills" data-step="fitment">
        ${fitments.map((f) => {
          const v = matching.find((m) => m.fitment === f);
          return `<button class="vyp__pill" type="button" data-handle="${this._esc(v.handle)}">${this._esc(LABELS[f] || f)}</button>`;
        }).join('')}
      </div>
      <button class="vyp__back" type="button">&larr; Back</button>
    `;

    this._revealPills(wrap.querySelector('[data-step="fitment"]'));
    wrap.querySelectorAll('[data-step="fitment"] .vyp__pill').forEach((btn) => {
      btn.addEventListener('click', () => this._navigate(btn.dataset.handle));
    });
    wrap.querySelector('.vyp__back').addEventListener('click', () => this._renderYears());
  }

  _navigate(handle) {
    window.location.href = `/pages/families/${handle}`;
  }

  async _revealPills(container) {
    const pills = container.querySelectorAll('.vyp__pill');
    for (const pill of pills) {
      await new Promise((r) => setTimeout(r, 80));
      pill.classList.add('vyp__pill--visible');
    }
  }

  _esc(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }
}

customElements.define('vehicle-year-picker', VehicleYearPicker);
